import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiDollarSign } = FiIcons;

const ClientsPipeline = ({ clients }) => {
  const stages = [
    { name: 'Lead', key: 'lead', color: 'bg-yellow-500' },
    { name: 'Proposal', key: 'proposal', color: 'bg-blue-500' },
    { name: 'Negotiation', key: 'negotiation', color: 'bg-purple-500' },
    { name: 'Active', key: 'active', color: 'bg-green-500' }
  ];

  const clientsByStage = stages.map(stage => ({
    ...stage,
    clients: clients.filter(client => client.stage === stage.key || (stage.key === 'active' && client.status === 'active')),
    value: clients
      .filter(client => client.stage === stage.key || (stage.key === 'active' && client.status === 'active'))
      .reduce((sum, client) => sum + (client.value || 0), 0)
  }));

  const totalValue = clients.reduce((sum, client) => sum + (client.value || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Sales Pipeline</h3>
        <div className="flex items-center space-x-1 text-green-600">
          <SafeIcon icon={FiDollarSign} className="w-5 h-5" />
          <span className="font-semibold">${totalValue.toLocaleString()}</span>
        </div>
      </div>

      <div className="space-y-4">
        {clientsByStage.map((stage, index) => (
          <motion.div
            key={stage.key}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
          >
            <div className="flex items-center space-x-3">
              <div className={`w-4 h-4 rounded-full ${stage.color}`} />
              <div>
                <h4 className="font-medium text-gray-900">{stage.name}</h4>
                <div className="flex items-center space-x-4 mt-1">
                  <div className="flex items-center space-x-1 text-gray-600">
                    <SafeIcon icon={FiUsers} className="w-4 h-4" />
                    <span className="text-sm">{stage.clients.length}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-gray-600">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                    <span className="text-sm">${stage.value.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-right">
              <div className="text-lg font-bold text-gray-900">
                {stage.clients.length}
              </div>
              <div className="text-sm text-gray-500">
                {totalValue > 0 ? Math.round((stage.value / totalValue) * 100) : 0}%
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {clients.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total Pipeline Value</span>
            <span className="font-semibold text-gray-900">
              ${totalValue.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ClientsPipeline;